import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { IconButton, Menu, MenuItem } from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import CommonDataTable from '../../../components/CommonDataTable';
import { getInventoryLedger } from '../../../services/item/item';

// Define columns (display names)
const rawColumns = [
  // action column injected separately

  { field: 'date', headerName: 'Date', width: 140 },
  { field: 'facilityId', headerName: 'Facility Id', width: 140 },
  { field: 'facilityName', headerName: 'Facility Name', width: 140 },

  {
    field: 'skuId',
    headerName: 'SKU ID',
    width: 100,
    renderCell: (params) => (
      <a
        href={`#/inventory/item/${encodeURIComponent(params.value)}`}
        style={{ textDecoration: 'none' }}
        onClick={(e) => e.stopPropagation()}
      >
        {params.value}
      </a>
    )
  },

  { field: 'description', headerName: 'Description', width: 200 },
  { field: 'uom', headerName: 'UOM', width: 100 },
  { field: 'openingStock', headerName: 'Opening Qty', width: 140 },
  { field: 'receivedQty', headerName: 'Inbound Qty', width: 140 },
  { field: 'issuedQty', headerName: 'Outbound Qty', width: 140 },
  { field: 'closingStock', headerName: 'Closing Stock', width: 140 },
  // { field: 'adjustedQty', headerName: 'Adjusted Qty', width: 140 },
  // { field: 'issuedQty', headerName: 'Issued Qty', width: 140 },
  // { field: 'receivedQty', headerName: 'Received Qty', width: 140 },
  // { field: 'adjustedQty', headerName: 'Adjusted Qty', width: 140 },
];

export default function InventoryLedgerList() {
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [menuRow, setMenuRow] = useState(null);
  const [tableRows, setTableRows] = useState([]);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [appliedFilters, setAppliedFilters] = useState([]);
  // ----------------- Date range state -----------------
  // Default: last one month through today (local date, so the picker matches the user's day)
  const formatLocalDate = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const today = new Date();
  const oneMonthAgo = new Date(today);
  oneMonthAgo.setMonth(today.getMonth() - 1);

  const defaultEnd = formatLocalDate(today);
  const defaultStart = formatLocalDate(oneMonthAgo);


  const [dateRange, setDateRange] = useState({
    start: defaultStart,
    end: defaultEnd,
  });

  // ----------------- Fetch handler -----------------
  const fetchHandler = useCallback(async (pageNo, pageSizeNo, filters = []) => {
    try {
      const res = await getInventoryLedger(
        dateRange.start,
        dateRange.end,
        pageNo,
        pageSizeNo,
        filters
      );
      const total = res.totalItems;

      const rows = res.data.map((item, index) => ({
        id: index,
        skuId: item._id.skuId,
        description: item._id.itemName,
        facilityId: item._id.facilityId,
        facilityName: item._id.facilityName,
        uom: item._id.uom,
        date: item._id.dayKey,
        adjustedQty: item.adjustedQty,
        closingStock: item.closingStock,
        issuedQty: item.issuedQty,
        openingStock: item.openingStock,
        receivedQty: item.receivedQty,
      }));

      setTableRows(rows);
      setTotalCount(total);
      console.log("Data refreshed successfully");
    } catch (error) {
      console.error("Error fetching Item Inventory data:", error);
    }
  }, [dateRange]); // 🔑 re-run when dateRange changes

  // Run on mount + whenever date range, page, or filters change
  useEffect(() => {
    fetchHandler(page, pageSize, appliedFilters);
  }, [fetchHandler, page, pageSize, appliedFilters]);

  const handleDataRefresh = useCallback((payload = {}) => {
    const nextFilters = Array.isArray(payload.filters) ? payload.filters : [];
    const nextSize = Number(payload.pageSize);
    setAppliedFilters(nextFilters);
    if (Number.isFinite(nextSize) && nextSize > 0 && nextSize !== pageSize) {
      setPageSize(nextSize);
    }
    setPage(0);
  }, [pageSize]);

  // ----------------- Menu handling -----------------
  const handleOpenMenu = (e, row) => {
    e.stopPropagation();
    setMenuAnchorEl(e.currentTarget);
    setMenuRow(row);
  };
  const handleCloseMenu = () => {
    setMenuAnchorEl(null);
    setMenuRow(null);
  };

  const actionColumn = {
    field: "__actions",
    headerName: "",
    width: 50,
    sortable: false,
    filterable: false,
    disableColumnMenu: true,
    renderCell: (params) => (
      <IconButton
        size="small"
        onClick={(e) => handleOpenMenu(e, params.row)}
        aria-label="row actions"
      >
        <MoreVertIcon fontSize="small" />
      </IconButton>
    ),
  };

  const allPossibleColumns = useMemo(
    () => [actionColumn, ...rawColumns],
    []
  );
  const initialVisible = useMemo(
    () => allPossibleColumns.map((c) => c.field),
    [allPossibleColumns]
  );

  return (
    <>
      <CommonDataTable
        allPossibleColumns={allPossibleColumns}
        initialVisible={initialVisible}
        initialRows={tableRows}
        height={520}
        rowsPerPageOptions={[10, 25, 50, 100]}
        exportImportPath="InventoryLedger"
        forItems
        forInventory
        enableDateRangeFilter
        defaultDateRange={dateRange}
        onDateRangeChange={(range) => {
          setDateRange(range);
          setPage(0);
        }}
        page={page}
        pageSize={pageSize}
        rowCount={totalCount}
        onPageChange={(newPage) => setPage(newPage)}
        onPageSizeChange={(newSize) => {
          setPageSize(newSize);
          setPage(0);
        }}
        onDataRefresh={handleDataRefresh}
        onManualRefresh={(payload) =>
          fetchHandler(
            payload?.page ?? page,
            payload?.pageSize ?? pageSize,
            payload?.filters ?? appliedFilters
          )
        }
      />

      <Menu
        open={Boolean(menuAnchorEl)}
        anchorEl={menuAnchorEl}
        onClose={handleCloseMenu}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
      />
    </>
  );
}
